// A dialog-like window on a phone is a sheet from the bottom, over the window in front (Josh 10-07, on the Android
// wrapper: tapping Add Server "puts a big modal in front of the UI" — Snap gave it a page of its own at the headset's
// size). The windows the headset shows as sheets are the ones placed 'front' (core/windows.js `place: [near, 'front']`:
// Add Server, New Window, Widgets, What's New, Choose a Robot). Under screen.phoneBelow, in Snap and in Look, each of
// them rises from the bottom of the stage: a scrim, a handle row with a ✕ (a 44 px touch, core/screen-phone.css), the
// window under it as wide as the screen and no taller than the band, its own content scrolling. The window behind
// stays where it is (the row does not move; a swipe on the sheet is the sheet's). A tablet, a desktop and the
// headset are untouched: there it is still a window in the row or on the arc.
//
// One seam: `new ScreenSheets(world)` from core/render-dom.js — `wants(spec)`, `place(rec)`, `fit(rec, S, size)`,
// `drop(rec)`, `raise(rec)`, `top()`. `sheetOnScreen(spec, width)` is the rule by itself (tools/tests/web-phone-sheets.mjs).
import { SCREEN } from './tokens.js'

const BAR = SCREEN.minTarget   // the handle row: the grabber and the ✕
const AIR = 12                 // above the sheet, the window behind shows (the card's own 8 px and its border)

/// Whether a window shows as a sheet at this width: placed 'front', with its own DOM, narrower than a phone's limit.
export const sheetOnScreen = (spec, width = innerWidth) => width < SCREEN.phoneBelow && Array.isArray(spec?.rule) && spec.rule[1] === 'front' && !!spec.dom

const X = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>'
const div = (cls) => { const d = document.createElement('div'); d.className = cls; return d }

export class ScreenSheets {
  constructor(world) {
    this.w = world
    this.open = []   // the sheets up, bottom to top (their recs)
    this.host = div('dw-sheets')
    this.host.hidden = true
    world.stage.appendChild(this.host)
    // a finger on a sheet is the sheet's: no swipe along the row behind it, no trackpad step
    for (const t of ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'wheel']) this.host.addEventListener(t, (e) => e.stopPropagation(), { passive: true })
  }

  wants(spec) { return sheetOnScreen(spec) }
  top() { return this.open.length ? this.open[this.open.length - 1].spec : null }

  /// The window's frame into a sheet, or back into its slot, as the width says. Returns whether it is a sheet now.
  place(rec) {
    const want = this.wants(rec.spec)
    if (want && !rec.sheet) {
      const name = rec.spec.short || rec.spec.name || 'window'
      const wrap = div('dw-sheet'), scrim = div('dw-sheet-scrim'), card = div('dw-sheet-card'), bar = div('dw-sheet-bar')
      const x = document.createElement('button')
      x.className = 'dw-sheet-x'
      x.innerHTML = X
      x.title = `Close ${name}`
      x.setAttribute('aria-label', `Close ${name}`)
      const close = () => this.w.app.closeWindow?.(rec.spec)
      x.addEventListener('click', close)
      scrim.addEventListener('click', close)
      bar.append(div('grab'), x)
      card.append(bar, rec.frame)
      wrap.append(scrim, card)
      rec.sheet = wrap
      this.open.push(rec)
      this.host.appendChild(wrap)
      this.host.hidden = false
    } else if (!want && rec.sheet) {
      this.drop(rec)
      rec.slot.appendChild(rec.frame)
    }
    return want
  }

  /// The sheet's window: as wide as the screen, no taller than the band less the handle row and the air above.
  fit(rec, S, size) {
    rec.el.style.width = S.w + 'px'
    rec.el.style.height = Math.max(120, Math.min(size.h, S.h - BAR - AIR)) + 'px'
    rec.el.classList.toggle('on-screen', true)
    rec.scale = 1
    rec.frame.style.transform = ''
  }

  /// The sheet in front of the others.
  raise(rec) {
    if (!rec.sheet || this.open[this.open.length - 1] === rec) return
    this.open = this.open.filter((r) => r !== rec)
    this.open.push(rec)
    this.host.appendChild(rec.sheet)
  }

  drop(rec) {
    if (!rec.sheet) return
    rec.sheet.remove()
    rec.sheet = null
    this.open = this.open.filter((r) => r !== rec)
    this.host.hidden = !this.open.length
  }
}
